import { Button, InputCheckbox, InputNumber } from "@sellar-io/components";
import type {
  ProductCreateRequestTypeProductItemType,
  ProductTemplateType,
} from "@sellar-io/api-v2/types";
import { ProductTypesFlat } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { useForm, useWatch } from "react-hook-form";
import { useCanonicalWeightCheck } from "../../../../../hooks/useCanonicalWeightCheck";
import { CanonicalWeightHint } from "../../../../../components/canonicalWeightHint/CanonicalWeightHint";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { ProductTemplateItem } from "./ProductTemplateItem";
import { Icon } from "../../../../../components";

export type ProductCreationItemDetailsType = Pick<
  ProductCreateRequestTypeProductItemType,
  | "containerType"
  | "volume"
  | "unit"
  | "packQuantity"
  | "weight"
  | "sku"
  | "vegan"
  | "lactoseFree"
  | "glutenFree"
  | "type"
>;

export default function ItemDetailsDialogContent({
  productCreationItemDetails,
  onBack,
  onDismiss,
  onSubmitProductItemDetails,
  template,
}: {
  productCreationItemDetails: Partial<ProductCreationItemDetailsType>;
  onBack: () => void;
  onDismiss: () => void;
  onSubmitProductItemDetails: (product: ProductCreationItemDetailsType) => void;
  template?: ProductTemplateType;
}) {
  const { register, handleSubmit, control, setValue } =
    useForm<ProductCreationItemDetailsType>({
      defaultValues: {
        ...productCreationItemDetails,
        ...(template
          ? {
              containerType: template.containerType,
              packQuantity: template.packQuantity,
              unit: template.displayUnit,
              volume: template.volume,
            }
          : {}),
      },
    });

  const vegan = !!useWatch({
    control,
    name: "vegan",
  });
  const glutenFree = !!useWatch({
    control,
    name: "glutenFree",
  });
  const lactoseFree = !!useWatch({
    control,
    name: "lactoseFree",
  });

  const containerType = useWatch({ control, name: "containerType" });
  const volume = useWatch({ control, name: "volume" });
  const unit = useWatch({ control, name: "unit" });
  const packQuantity = useWatch({ control, name: "packQuantity" });
  const weight = useWatch({ control, name: "weight" });

  const { canonicalWeight, weightDeviatesFromCanonical } =
    useCanonicalWeightCheck({
      containerType,
      unit,
      volume,
      packQuantity,
      weight,
    });

  const effectiveWeight =
    weight != null && !Number.isNaN(weight)
      ? weight
      : (canonicalWeight ?? undefined);

  return (
    <form
      onSubmit={handleSubmit((product) =>
        onSubmitProductItemDetails({
          ...product,
          weight: effectiveWeight,
          ...(template
            ? {
                containerType: template.containerType,
                packQuantity: template.packQuantity,
                unit: template.displayUnit,
                volume: template.volume,
              }
            : {}),
        }),
      )}
    >
      <DialogNavigation
        title={`Item details`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        <div className={"flex flex-col gap-2"}>
          {!!template ? (
            <>
              <ProductTemplateItem
                template={template}
                className="border border-zinc-300"
              />
              <input type="hidden" {...register("containerType")} />
              <input
                type="hidden"
                {...register("volume", { valueAsNumber: true })}
              />
              <input type="hidden" {...register("unit")} />
              <input
                type="hidden"
                {...register("packQuantity", { valueAsNumber: true })}
              />
            </>
          ) : (
            <>
              <div>
                <InputLabel>Container type</InputLabel>
                <InputContainer>
                  <InputSelect
                    defaultValue={""}
                    {...register("containerType")}
                    required={true}
                  >
                    <option
                      value={""}
                      selected={true}
                      disabled={true}
                      hidden={true}
                    >
                      {"Choose..."}
                    </option>
                    {ProductTypesFlat.map((item) => (
                      <InputOption key={item.key} value={item.key}>
                        {item.display}
                      </InputOption>
                    ))}
                  </InputSelect>
                </InputContainer>
              </div>

              <div>
                <div>
                  <InputLabel>Volume</InputLabel>
                  <InputContainer className={"w-full"}>
                    <InputNumber
                      {...register("volume", {
                        valueAsNumber: true,
                      })}
                      min={0.01}
                      step={0.01}
                      required={true}
                    />
                  </InputContainer>
                </div>

                <div>
                  <InputLabel>Unit</InputLabel>
                  <InputContainer>
                    <InputSelect
                      {...register("unit")}
                      required={true}
                      defaultValue={""}
                    >
                      <option
                        value={""}
                        selected={true}
                        disabled={true}
                        hidden={true}
                      >
                        {"Choose..."}
                      </option>
                      <InputOption value={"gal"}>gal - gallon</InputOption>
                      <InputOption value={"L"}>L - litre</InputOption>
                      <InputOption value={"ml"}>ml - millilitre</InputOption>
                      <InputOption value={"cl"}>cl - centilitre</InputOption>
                      <InputOption value={"pt"}>pt - pint</InputOption>
                      <InputOption value={"oz"}>oz - ounce</InputOption>
                      <InputOption value={"g"}>g - gram</InputOption>
                      <InputOption value={"kg"}>kg - kilogram</InputOption>
                    </InputSelect>
                  </InputContainer>
                </div>
              </div>

              <div>
                <InputLabel>Pack Quantity</InputLabel>
                <InputContainer className={"w-full"}>
                  <InputNumber
                    {...register("packQuantity", {
                      valueAsNumber: true,
                    })}
                    min={1}
                    required={true}
                  />
                </InputContainer>
              </div>
            </>
          )}

          <div className="grid w-full grid-cols-2 gap-2">
            <div>
              <InputLabel>Weight (kg)</InputLabel>
              <InputContainer className={"w-full"}>
                <InputNumber
                  {...register("weight", {
                    valueAsNumber: true,
                  })}
                  placeholder={canonicalWeight?.toString()}
                  step={0.01}
                  required={false}
                />
              </InputContainer>
              <CanonicalWeightHint
                canonicalWeight={canonicalWeight}
                weightDeviatesFromCanonical={weightDeviatesFromCanonical}
                weight={weight}
              />
            </div>
            <div>
              <InputLabel>SKU</InputLabel>
              <InputContainer className={"w-full"}>
                <InputText {...register("sku")} required={false} />
              </InputContainer>
            </div>
          </div>

          <div className={"divide-y rounded-lg border border-solid"}>
            <input type="hidden" {...register("vegan")} />
            <input type="hidden" {...register("lactoseFree")} />
            <input type="hidden" {...register("glutenFree")} />
            <div
              className={twMerge(
                "flex flex-row justify-between p-4",
                vegan && "bg-gray-100",
              )}
              onClick={() => setValue("vegan", !vegan)}
            >
              <InputLabel>Vegan</InputLabel>
              <InputCheckbox checked={vegan} readOnly />
            </div>

            <div
              className={twMerge(
                "flex flex-row justify-between p-4",
                glutenFree && "bg-gray-100",
              )}
              onClick={() => setValue("glutenFree", !glutenFree)}
            >
              <InputLabel>Gluten free</InputLabel>
              <InputCheckbox checked={glutenFree} readOnly />
            </div>

            <div
              className={twMerge(
                "flex flex-row justify-between p-4",
                lactoseFree && "bg-gray-100",
              )}
              onClick={() => setValue("lactoseFree", !lactoseFree)}
            >
              <InputLabel>Lactose free</InputLabel>
              <InputCheckbox checked={lactoseFree} readOnly />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex items-center justify-between">
        <Button color="tertiary" onClick={onBack}>
          <div className="flex">
            <Icon icon="CHEVRON_LEFT" />
            <div>Back</div>
          </div>
        </Button>
        <Button type="submit">Continue</Button>
      </DialogCTA>
    </form>
  );
}
